import * as Sentry from '@sentry/react';
import { useEffect } from 'react';
import {
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from 'react-router-dom';
import { ApiError } from '@/api/errors';

const dsn = import.meta.env.VITE_SENTRY_DSN;
const env = import.meta.env.VITE_SENTRY_ENVIRONMENT ?? import.meta.env.MODE;

const SUPPRESSED_API_CODES = new Set([
  'NETWORK_ERROR',
  'NETWORK_INTERRUPTED',
  'HTTP_502',
  'HTTP_503',
  'HTTP_504',
]);

function scrubTokenFromUrl(url: string): string {
  return url.replace(/([?&])token=[^&]+/g, '$1token=[REDACTED]');
}

if (dsn && import.meta.env.MODE !== 'test') {
  Sentry.init({
    dsn,
    environment: env,
    release: import.meta.env.VITE_SENTRY_RELEASE,
    sendDefaultPii: false,
    integrations: [
      Sentry.reactRouterV7BrowserTracingIntegration({
        useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),
      Sentry.replayIntegration({ maskAllText: true, blockAllMedia: true }),
    ],
    tracesSampleRate: env === 'production' ? 0.1 : 1.0,
    tracePropagationTargets: ['localhost', /^\/api\//, /^https:\/\/portal\.thermoduct\.be\/api/],
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 1.0,
    beforeSend(event, hint) {
      // Suppress expected deploy-class / user-cancelled network errors so they
      // don't spam Sentry on every API restart. Check hint.originalException
      // (the actual Error reference) because the serialized `event` loses class
      // identity.
      const err = hint?.originalException;
      if (err instanceof ApiError && SUPPRESSED_API_CODES.has(err.code)) {
        return null;
      }
      // EventSource errors from /api/admin/activity/stream?token=<jwt> would leak the JWT into Sentry
      if (event.request?.url) event.request.url = scrubTokenFromUrl(event.request.url);
      return event;
    },
    beforeBreadcrumb(breadcrumb) {
      // Fetch/xhr/navigation breadcrumbs may carry the SSE URL with the token
      if (typeof breadcrumb.data?.url === 'string') {
        breadcrumb.data.url = scrubTokenFromUrl(breadcrumb.data.url);
      }
      if (typeof breadcrumb.data?.to === 'string') {
        breadcrumb.data.to = scrubTokenFromUrl(breadcrumb.data.to);
      }
      if (typeof breadcrumb.data?.from === 'string') {
        breadcrumb.data.from = scrubTokenFromUrl(breadcrumb.data.from);
      }
      return breadcrumb;
    },
  });
}

interface UserLike {
  id: string;
  name: string;
  email: string;
  roles: string[];
}

// Sentry rejects empty-string tag values ("Discarded invalid value: expected a non-empty value").
// Helper sets the tag if value is non-empty, otherwise removes it from the current scope.
function setTagOrClear(key: string, value: string | null | undefined): void {
  if (value !== null && value !== undefined && value !== '') {
    Sentry.setTag(key, value);
  } else {
    Sentry.getCurrentScope().setTag(key, undefined);
  }
}

export function attachSentryUser(user: UserLike | null): void {
  if (!user) {
    Sentry.setUser(null);
    setTagOrClear('roles', null);
    setTagOrClear('impersonating', null);
    setTagOrClear('impersonated_roles', null);
    return;
  }
  Sentry.setUser({ id: user.id, username: user.name, email: user.email });
  setTagOrClear('roles', user.roles.join(','));

  let impersonating = false;
  let impersonatedRoles = '';
  if (typeof localStorage !== 'undefined') {
    try {
      const override = localStorage.getItem('dev-role-override');
      if (override) {
        const parsed = JSON.parse(override);
        if (Array.isArray(parsed?.roles) && parsed.roles.length > 0) {
          impersonating = true;
          impersonatedRoles = parsed.roles.join(',');
        }
      }
    } catch { /* malformed override — ignore */ }
  }
  setTagOrClear('impersonating', impersonating ? 'true' : 'false');
  setTagOrClear('impersonated_roles', impersonatedRoles);
}
